import { motion } from "framer-motion";
import { ArrowUpRight, Activity, Layers, LineChart, CandlestickChart, Globe2, ShieldCheck, Instagram, Send, Award } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, SectionLabel } from "../Reveal";
import { BRAND } from "../../config/brand";
import { FOUNDER_PHOTO, FOUNDER_SOCIALS, FOUNDER_CREDENTIALS } from "../../i18n/founder";

const APPROACH_ICONS = [Activity, Layers, LineChart, CandlestickChart, Globe2, ShieldCheck];
const SOCIAL_ICONS = { website: Globe2, instagram: Instagram, instagramSynrise: Instagram, telegram: Send };
const BODY = "text-base leading-relaxed text-zinc-300 sm:text-lg";

const SectionHead = ({ index, title }) => (
  <Reveal>
    <SectionLabel index={index}>{title}</SectionLabel>
    <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-zinc-50 sm:text-4xl">{title}</h2>
  </Reveal>
);

export const FounderPortrait = ({ alt }) => (
  <motion.figure initial={{ opacity: 0.4, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
    className="relative mx-auto w-full max-w-[280px] sm:max-w-[340px] lg:max-w-[400px]" data-testid="founder-portrait">
    <div className="absolute -inset-3 rounded-[40px] border border-gold/25 sm:-inset-4" />
    <div className="absolute -inset-10 -z-10 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.18)_0%,transparent_65%)]" />
    <div className="relative overflow-hidden rounded-[32px] border border-white/10 bg-surface">
      <picture>
        <source srcSet={FOUNDER_PHOTO.webp} type="image/webp" />
        <img src={FOUNDER_PHOTO.jpg} alt={alt} width={FOUNDER_PHOTO.width} height={FOUNDER_PHOTO.height} fetchPriority="high"
          className="block aspect-[4/5] w-full object-cover" data-testid="founder-image" />
      </picture>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-ink/90 via-ink/30 to-transparent" />
    </div>
    <figcaption className="absolute -bottom-5 left-5 flex items-center gap-3 rounded-full border border-gold/30 bg-ink/90 py-1.5 pl-1.5 pr-4 backdrop-blur-xl">
      <img src={BRAND.logoSmall} alt="" aria-hidden="true" className="h-8 w-8" />
      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">Founder & CEO</span>
    </figcaption>
  </motion.figure>
);

export const JourneySection = ({ c }) => (
  <section className="grid gap-10 lg:grid-cols-12" data-testid="founder-journey">
    <Reveal className="lg:col-span-4"><SectionLabel index="01">{c.journeyTitle}</SectionLabel></Reveal>
    <div className="space-y-5 lg:col-span-8">
      <h2 className="sr-only">{c.journeyTitle}</h2>
      {c.journey.map((p) => <Reveal key={p.slice(0, 24)}><p className={BODY}>{p}</p></Reveal>)}
    </div>
  </section>
);

export const ApproachSection = ({ c }) => (
  <section data-testid="founder-approach">
    <SectionHead index="02" title={c.approachTitle} />
    <Reveal><p className={`mt-5 max-w-3xl ${BODY}`}>{c.approachLead}</p></Reveal>
    <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {c.approach.map(([title, text], i) => {
        const Icon = APPROACH_ICONS[i];
        return (
          <Reveal key={title} delay={i * 0.05}>
            <article className="h-full rounded-[28px] border border-white/[0.07] bg-surface/70 p-6 transition-colors duration-500 hover:border-gold/30" data-testid={`founder-approach-${i}`}>
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-gold/30 bg-gold/10"><Icon className="h-5 w-5 text-gold" /></span>
              <h3 className="mt-5 font-display text-lg font-semibold text-zinc-50">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-400">{text}</p>
            </article>
          </Reveal>
        );
      })}
    </div>
    <div className="mt-10 grid gap-8 lg:grid-cols-12">
      <Reveal className="lg:col-span-7">
        <p className="border-l-2 border-gold/60 pl-5 font-serif text-xl italic leading-snug text-zinc-100 sm:text-2xl" data-testid="founder-philosophy">{c.philosophy}</p>
      </Reveal>
      <Reveal className="lg:col-span-5" delay={0.08}>
        <ul className="flex flex-wrap gap-2" data-testid="founder-assets">
          {c.assets.map((a) => <li key={a} className="rounded-full border border-gold/25 bg-gold/[0.07] px-3.5 py-1.5 text-[13px] text-zinc-100">{a}</li>)}
        </ul>
        <p className="mt-4 text-sm leading-relaxed text-zinc-400">{c.assetsText}</p>
      </Reveal>
    </div>
  </section>
);

export const OutreachSection = ({ c }) => (
  <section className="grid gap-10 lg:grid-cols-12" data-testid="founder-outreach">
    <div className="lg:col-span-7">
      <SectionHead index="03" title={c.outreachTitle} />
      <div className="mt-6 space-y-5">{c.outreach.map((p) => <Reveal key={p.slice(0, 24)}><p className={BODY}>{p}</p></Reveal>)}</div>
    </div>
    <Reveal className="lg:col-span-5 lg:pt-16" delay={0.08}>
      <blockquote className="rounded-[28px] border border-gold/20 bg-surface/70 p-6 sm:p-8">
        <p className="font-serif text-2xl italic leading-snug text-zinc-100">“{c.outreachQuote}”</p>
      </blockquote>
      <p className="mt-4 text-[13px] leading-relaxed text-zinc-400" data-testid="founder-outreach-note">{c.outreachNote}</p>
    </Reveal>
  </section>
);

export const TrainingSection = ({ c }) => (
  <section data-testid="founder-training">
    <SectionHead index="04" title={c.trainingTitle} />
    <div className="mt-6 grid gap-10 lg:grid-cols-12">
      <div className="space-y-5 lg:col-span-6">
        <Reveal><p className={BODY}>{c.trainingIntro}</p></Reveal>
        {c.training.map((p) => <Reveal key={p.slice(0, 24)}><p className={BODY}>{p}</p></Reveal>)}
      </div>
      <Reveal className="lg:col-span-6" delay={0.08}>
        <div className="rounded-[28px] border border-white/[0.07] bg-surface/70 p-6 sm:p-8">
          <p className="text-sm text-zinc-400">{c.credentialsLabel}</p>
          <ul className="mt-5 divide-y divide-white/[0.07]" data-testid="founder-credentials">
            {FOUNDER_CREDENTIALS.map((name) => (
              <li key={name} className="flex items-center gap-4 py-3.5">
                <Award className="h-5 w-5 shrink-0 text-gold" />
                <span className="font-display text-base font-semibold text-zinc-50">{name}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">Evolution Forum</p>
        </div>
      </Reveal>
    </div>
  </section>
);

export const SynriseSection = ({ c }) => (
  <section className="relative overflow-hidden rounded-[36px] border border-gold/20 bg-surface/70 px-6 py-12 sm:px-12 sm:py-16" data-testid="founder-synrise">
    <Reveal>
      <SectionLabel index="05">SYNRISE</SectionLabel>
      <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-zinc-50 sm:text-4xl">{c.synTitle}</h2>
      {c.synText.map((p) => <p key={p.slice(0, 24)} className="mt-5 max-w-3xl text-base leading-relaxed text-zinc-300">{p}</p>)}
      <p className="mt-8 text-sm text-zinc-400">{c.pillarsLead}</p>
    </Reveal>
    <div className="mt-6 grid gap-6 md:grid-cols-3">
      {c.pillars.map(([word, text], i) => (
        <Reveal key={word} delay={i * 0.08}>
          <div className="border-t border-gold/30 pt-5" data-testid={`founder-pillar-${i}`}>
            <p className="font-serif text-4xl italic text-gold">{word}</p>
            <p className="mt-3 text-sm leading-relaxed text-zinc-300">{text}</p>
          </div>
        </Reveal>
      ))}
    </div>
    <Reveal><p className="mt-10 max-w-3xl text-base leading-relaxed text-zinc-300" data-testid="founder-collab">{c.collab}</p></Reveal>
    <div className="mt-8 flex flex-wrap items-center gap-6">
      <Link to="/" data-testid="founder-cta" className="inline-flex h-12 items-center gap-2 rounded-full bg-gold px-6 text-sm font-semibold text-ink transition-opacity hover:opacity-90">
        {c.cta} <ArrowUpRight className="h-4 w-4" />
      </Link>
      <p className="font-mono text-[12px] uppercase tracking-[0.3em] text-gold" data-testid="founder-tagline">{c.tagline}</p>
    </div>
  </section>
);

export const VisionSection = ({ c }) => (
  <section className="grid gap-10 lg:grid-cols-12" data-testid="founder-vision">
    <Reveal className="lg:col-span-4"><SectionLabel index="06">{c.visionTitle}</SectionLabel></Reveal>
    <Reveal className="lg:col-span-8">
      <h2 className="sr-only">{c.visionTitle}</h2>
      <p className="font-serif text-2xl italic leading-snug text-zinc-100 sm:text-3xl">{c.vision}</p>
    </Reveal>
  </section>
);

export const SocialsSection = ({ c }) => (
  <section data-testid="founder-socials">
    <Reveal>
      <SectionLabel index="07">{c.socialTitle}</SectionLabel>
      <h2 className="sr-only">{c.socialTitle}</h2>
    </Reveal>
    <div className="mt-6 grid gap-3 sm:grid-cols-2">
      {FOUNDER_SOCIALS.map((s) => {
        const Icon = SOCIAL_ICONS[s.id] || Globe2;
        return (
          <a key={s.id} href={s.url} target="_blank" rel={s.owner === "person" ? "noopener noreferrer me" : "noopener noreferrer"} data-testid={`founder-social-${s.id}`}
            className="group flex items-center gap-4 rounded-[24px] border border-white/[0.07] bg-surface/70 p-4 transition-colors hover:border-gold/30">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04]"><Icon className="h-5 w-5 text-gold" /></span>
            <span className="min-w-0 flex-1">
              <span className="block font-display text-base font-semibold text-zinc-50">{c.socialLabels[s.id]}</span>
              <span className="block truncate text-sm text-zinc-400">{s.handle}</span>
            </span>
            <ArrowUpRight className="h-5 w-5 text-zinc-400 transition-transform group-hover:rotate-45 group-hover:text-gold" />
          </a>
        );
      })}
    </div>
  </section>
);
